import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box } from '@mui/material';
import dayjs from 'dayjs';

import { useSnackbar } from '@/hooks/useSnackbar';

import { PublicSignerLoadingState } from './components/PublicSignerLoadingState';
import { PublicSignerStateCard } from './components/PublicSignerStateCard';
import { PublicSignerWorkspace } from './components/PublicSignerWorkspace';
import { useDisableDocumentOverscroll } from './hooks/useDisableDocumentOverscroll';
import { usePublicESignSigner } from './hooks/usePublicESignSigner';
import { buildPublicSignerSubmitRequest } from './utils/buildSubmitRequest';
import {
  buildFieldDraftValues,
  buildFieldErrorMap,
  collectUnboundValidationMessages,
  filterFieldErrorsForActiveFields,
  getPublicSignerSessionStateKey,
  mergeFieldDraftValues
} from './utils/fieldDraft';
import {
  findFirstInvalidFieldId,
  findNextFieldId,
  findPreviousFieldId,
  getOrderedEditableFields
} from './utils/fieldOrdering';
import { isFieldEditable } from './utils/fieldValue';
import {
  getCompletionPresentation,
  getLinkStatePresentation
} from './utils/presentation';
import type {
  PublicSignerFieldDraftValue,
  PublicSignerFieldDraftValues,
  PublicSignerFieldErrorMap
} from './utils/types';

import {
  isTerminalLinkState,
  type PublicSignerError
} from './publicSignerError';
import {
  EsignFieldKind,
  EsignSignerStatus,
  type PublicSignerSignatureValue,
  type PublicSignerSubmitResponse
} from './publicSignerTypes';

type PublicSignerSignaturePresetMode = 'typed' | 'drawn';

export default function PublicESignSignerPage() {
  const { token } = useParams<{ token: string }>();
  const { sessionQuery, submitMutation } = usePublicESignSigner(token);
  const resetSubmitMutation = submitMutation.reset;
  const bootstrappedSessionKeyRef = useRef<string | null>(null);
  const [fieldDraftValues, setFieldDraftValues] =
    useState<PublicSignerFieldDraftValues>({});
  const [fieldErrors, setFieldErrors] = useState<PublicSignerFieldErrorMap>({});
  const [hasPendingDisclosureRetry, setHasPendingDisclosureRetry] =
    useState(false);
  const [isDisclosureAccepted, setIsDisclosureAccepted] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [submitResult, setSubmitResult] =
    useState<PublicSignerSubmitResponse | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [selectedFieldScrollRequestId, setSelectedFieldScrollRequestId] =
    useState(0);
  const [openSignatureFieldId, setOpenSignatureFieldId] = useState<
    string | null
  >(null);
  // Cross-field signature reuse: once the signer applies a typed or drawn
  // signature on any field, it becomes the default preset for the next
  // unsigned field. Updated only on Apply with a non-null value (Cancel
  // and Clear+Apply do not overwrite the preset). Auto-fill is preview-
  // only — the field's draftValue stays null until the user explicitly
  // clicks Apply on that field, so isFieldComplete still requires an
  // affirmative action per signature.
  const [lastTypedName, setLastTypedName] = useState<string | null>(null);
  const [lastDrawnImageDataUrl, setLastDrawnImageDataUrl] = useState<
    string | null
  >(null);
  const [lastAppliedSignatureMode, setLastAppliedSignatureMode] =
    useState<PublicSignerSignaturePresetMode | null>(null);

  const handleSignatureApplied = useCallback(
    (value: PublicSignerSignatureValue | null) => {
      if (!value) {
        return;
      }
      if (value.type === 'typed') {
        setLastTypedName(value.typed_name);
        setLastAppliedSignatureMode('typed');
        return;
      }
      setLastDrawnImageDataUrl(value.drawn_image_data_url);
      setLastAppliedSignatureMode('drawn');
    },
    []
  );

  useDisableDocumentOverscroll();

  useEffect(() => {
    if (!sessionQuery.data) {
      return;
    }

    const sessionStateKey = getPublicSignerSessionStateKey(
      sessionQuery.data,
      token
    );
    const isNewSession = bootstrappedSessionKeyRef.current !== sessionStateKey;

    setFieldDraftValues((previousFieldDraftValues) => {
      const nextFieldDraftValues = isNewSession
        ? buildFieldDraftValues(sessionQuery.data.fields)
        : mergeFieldDraftValues(
            sessionQuery.data.fields,
            previousFieldDraftValues
          );

      setSelectedFieldId((previousSelectedFieldId) =>
        previousSelectedFieldId &&
        sessionQuery.data.fields.some(
          (field) =>
            isFieldEditable(field) && field.id === previousSelectedFieldId
        )
          ? previousSelectedFieldId
          : null
      );

      return nextFieldDraftValues;
    });
    setFieldErrors((previousFieldErrors) =>
      isNewSession
        ? {}
        : filterFieldErrorsForActiveFields(
            previousFieldErrors,
            sessionQuery.data.fields
          )
    );
    setHasPendingDisclosureRetry(false);
    setIsDisclosureAccepted((previousValue) =>
      isNewSession ? false : previousValue
    );
    setSubmitResult((previousValue) => (isNewSession ? null : previousValue));
    setOpenSignatureFieldId((previousOpenSignatureFieldId) =>
      previousOpenSignatureFieldId &&
      sessionQuery.data.fields.some(
        (field) =>
          isFieldEditable(field) &&
          field.field_kind === EsignFieldKind.Signature &&
          field.id === previousOpenSignatureFieldId
      )
        ? previousOpenSignatureFieldId
        : null
    );

    if (isNewSession) {
      resetSubmitMutation();
      setLastTypedName(null);
      setLastDrawnImageDataUrl(null);
      setLastAppliedSignatureMode(null);
    }

    bootstrappedSessionKeyRef.current = sessionStateKey;
  }, [resetSubmitMutation, sessionQuery.data, token]);

  const sessionExpiresAt = sessionQuery.data?.session_expires_at;
  const refetchSession = sessionQuery.refetch;
  // Date instances change identity per refetch even when the time value is
  // equal, which would loop the past-expiry refetch branch. Track only
  // past-expiry refetches by value, NOT the future-expiry branch — the
  // cleanup below cancels each timer, so future-expiry must always
  // re-schedule on every effect run.
  const lastPastExpiryRefetchMsRef = useRef<number | null>(null);

  useEffect(() => {
    if (!sessionExpiresAt) {
      return;
    }

    const expiry = dayjs(sessionExpiresAt);
    if (!expiry.isValid()) {
      return;
    }

    const expiryMs = expiry.valueOf();
    if (!Number.isFinite(expiryMs)) {
      return;
    }

    const remainingMs = expiryMs - Date.now();

    if (remainingMs <= 0) {
      if (lastPastExpiryRefetchMsRef.current === expiryMs) {
        return;
      }
      lastPastExpiryRefetchMsRef.current = expiryMs;
      refetchSession();
      return;
    }

    // setTimeout delay is a 32-bit signed int; > 2^31-1 clamps to 1ms.
    // Guard the scheduled delay, not remainingMs alone — the +1s buffer
    // can push a near-limit value over.
    const MAX_SAFE_TIMEOUT = 2_147_483_647;
    const scheduledDelay = remainingMs + 1000;
    if (scheduledDelay > MAX_SAFE_TIMEOUT) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      refetchSession();
    }, scheduledDelay);

    return () => window.clearTimeout(timeoutId);
  }, [refetchSession, sessionExpiresAt]);

  const requiresDisclosure = useMemo(
    () => !isDisclosureAccepted,
    [isDisclosureAccepted]
  );

  const handleFieldDraftChange = useCallback(
    (fieldId: string, value: PublicSignerFieldDraftValue) => {
      setFieldDraftValues((prev) => ({
        ...prev,
        [fieldId]: value
      }));
      setFieldErrors((prev) => {
        if (!prev[fieldId]) {
          return prev;
        }

        const nextErrors = { ...prev };
        delete nextErrors[fieldId];
        return nextErrors;
      });
    },
    []
  );

  const handleAdvanceToNextField = useCallback(() => {
    if (!sessionQuery.data) {
      return;
    }

    setSelectedFieldId(
      findNextFieldId(
        getOrderedEditableFields(sessionQuery.data.fields),
        selectedFieldId
      )
    );
    setSelectedFieldScrollRequestId((currentValue) => currentValue + 1);
  }, [selectedFieldId, sessionQuery.data]);

  const handleAdvanceToPreviousField = useCallback(() => {
    if (!sessionQuery.data) {
      return;
    }

    setSelectedFieldId(
      findPreviousFieldId(
        getOrderedEditableFields(sessionQuery.data.fields),
        selectedFieldId
      )
    );
    setSelectedFieldScrollRequestId((currentValue) => currentValue + 1);
  }, [selectedFieldId, sessionQuery.data]);

  const submitCurrentState = useCallback(
    async (disclosureAccepted = isDisclosureAccepted) => {
      if (!token || !sessionQuery.data) {
        return;
      }

      setFieldErrors({});

      try {
        const nextSubmitResult = await submitMutation.mutateAsync(
          buildPublicSignerSubmitRequest(
            sessionQuery.data,
            fieldDraftValues,
            disclosureAccepted
          )
        );

        setHasPendingDisclosureRetry(false);
        setSubmitResult(nextSubmitResult);
      } catch (error) {
        const publicSignerError = error as PublicSignerError;
        const disclosureRequiredError =
          publicSignerError.validationErrors?.find(
            (validationError) => validationError.code === 'disclosure_required'
          ) ?? null;
        const nextFieldErrors = buildFieldErrorMap(
          publicSignerError.validationErrors
        );

        setFieldErrors(nextFieldErrors);

        const firstErrorFieldId = sessionQuery.data
          ? findFirstInvalidFieldId(sessionQuery.data.fields, nextFieldErrors)
          : null;
        if (firstErrorFieldId) {
          setSelectedFieldId(firstErrorFieldId);
          setSelectedFieldScrollRequestId((currentValue) => currentValue + 1);
        }

        if (disclosureRequiredError) {
          setHasPendingDisclosureRetry(true);
          setIsDisclosureAccepted(false);
          showSnackbar({
            message: disclosureRequiredError.message,
            severity: 'error'
          });
          return;
        }

        const hasFieldRequiredError =
          publicSignerError.validationErrors?.some(
            (validationError) => validationError.code === 'field_required'
          ) ?? false;

        if (hasFieldRequiredError) {
          showSnackbar({
            message:
              'Your progress has been saved. Please complete the required fields and try again.',
            severity: 'error'
          });
          await sessionQuery.refetch();
          return;
        }

        if (
          publicSignerError.errorCode === 'validation_failed' ||
          !isTerminalLinkState(publicSignerError.linkState)
        ) {
          const unboundMessages = collectUnboundValidationMessages(
            publicSignerError.validationErrors
          );
          const combined = [publicSignerError.message, ...unboundMessages]
            .filter(Boolean)
            .join(' ');
          showSnackbar({
            message: combined || publicSignerError.message,
            severity: 'error'
          });
        }
      }
    },
    [
      fieldDraftValues,
      isDisclosureAccepted,
      sessionQuery,
      showSnackbar,
      submitMutation,
      token
    ]
  );

  const handleSubmit = useCallback(async () => {
    await submitCurrentState();
  }, [submitCurrentState]);

  const terminalMutationError =
    submitMutation.error &&
    isTerminalLinkState(
      (submitMutation.error as PublicSignerError).linkState
    ) &&
    (submitMutation.error as PublicSignerError).errorCode !==
      'validation_failed'
      ? (submitMutation.error as PublicSignerError)
      : null;

  // A transient background-refetch failure (server_error / rate_limited)
  // leaves sessionQuery.data intact and must NOT eject the signer from
  // the signing UI mid-session. On initial load (no data) we still surface
  // the error so the signer sees what went wrong.
  const sessionError = sessionQuery.error as PublicSignerError | null;
  const terminalSessionError =
    sessionError &&
    (!sessionQuery.data || isTerminalLinkState(sessionError.linkState))
      ? sessionError
      : null;

  const terminalError = terminalSessionError ?? terminalMutationError;

  if (sessionQuery.isLoading) {
    return <PublicSignerLoadingState />;
  }

  if (submitResult) {
    const completionPresentation = getCompletionPresentation(submitResult);

    return (
      <PublicSignerStateCard
        description={completionPresentation.description}
        icon={<CheckCircleIcon fontSize="large" />}
        severity="success"
        title={completionPresentation.title}
      />
    );
  }

  if (terminalError || !sessionQuery.data) {
    const presentation = getLinkStatePresentation(terminalError?.linkState);

    return (
      <PublicSignerStateCard
        description={terminalError?.message || presentation.description}
        icon={
          presentation.severity === 'success' ? (
            <CheckCircleIcon fontSize="large" />
          ) : undefined
        }
        severity={presentation.severity}
        title={presentation.title}
      />
    );
  }

  if (sessionQuery.data.signer_status === EsignSignerStatus.Completed) {
    return (
      <PublicSignerStateCard
        description="This signer has already completed the document."
        icon={<CheckCircleIcon fontSize="large" />}
        severity="success"
        title="Signing complete"
      />
    );
  }

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.info.main,
        height: '100vh',
        overflow: 'hidden'
      })}
    >
      <PublicSignerWorkspace
        fieldDraftValues={fieldDraftValues}
        fieldErrors={fieldErrors}
        isSubmitting={submitMutation.isLoading}
        onAdvanceToNextField={handleAdvanceToNextField}
        onAdvanceToPreviousField={handleAdvanceToPreviousField}
        onContinueDisclosure={async () => {
          setIsDisclosureAccepted(true);
          if (hasPendingDisclosureRetry) {
            await submitCurrentState(true);
            return;
          }

          setHasPendingDisclosureRetry(false);
        }}
        lastAppliedSignatureMode={lastAppliedSignatureMode}
        lastDrawnImageDataUrl={lastDrawnImageDataUrl}
        lastTypedName={lastTypedName}
        onFieldDraftChange={handleFieldDraftChange}
        onOpenSignatureField={setOpenSignatureFieldId}
        onRefetchSession={sessionQuery.refetch}
        onSelectField={setSelectedFieldId}
        onSignatureApplied={handleSignatureApplied}
        onSubmit={handleSubmit}
        openSignatureFieldId={openSignatureFieldId}
        requiresDisclosure={Boolean(requiresDisclosure)}
        selectedFieldId={selectedFieldId}
        selectedFieldScrollRequestId={selectedFieldScrollRequestId}
        session={sessionQuery.data}
      />
    </Box>
  );
}
